import { useEffect, useState } from "react";

/**
 * "Install on this phone" button.
 *
 * Three platforms, three behaviours:
 *   • Android Chrome / Edge / Brave: captures the browser's
 *     `beforeinstallprompt` event and exposes it as a one-tap
 *     install. Hides itself entirely if the browser hasn't
 *     decided we're install-eligible yet (PWA criteria not met,
 *     already installed, etc.).
 *   • iOS Safari: Apple does not support beforeinstallprompt and
 *     forbids programmatic install. We detect iOS-Safari and
 *     swap to a small instruction sheet ("Tap the Share icon,
 *     then 'Add to Home Screen'"). Apple users still install in
 *     two taps — they just have to tap the share button
 *     themselves.
 *   • Already running standalone (PWA opened from home screen)
 *     OR desktop browser: button is hidden, since installing
 *     on top of itself is meaningless.
 *
 * Usage: drop <InstallPWAButton /> anywhere in the page tree. It
 * self-hides until it has something useful to do, so you don't
 * need to gate it.
 */
export default function InstallPWAButton({ className = "" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installState, setInstallState] = useState("idle"); // idle | installed | dismissed
  const [isIOSSafari, setIsIOSSafari] = useState(false);
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  useEffect(() => {
    // Detect iOS-Safari (NOT Chrome on iOS, which uses the same
    // WebKit engine but is reported as CriOS in the UA). Apple's
    // home-screen install only works in real Safari.
    const ua = window.navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const safari = /^((?!chrome|crios|fxios|edgios).)*safari/i.test(ua);
    setIsIOSSafari(iOS && safari);

    // If we're already running standalone, the user has already
    // installed us — don't pester them.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstallState("installed");
      return;
    }

    const onBeforeInstallPrompt = (e) => {
      // Stash the event so we can fire .prompt() on user gesture.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstallState("installed");
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setInstallState("installed");
      } else {
        setInstallState("dismissed");
      }
    } catch {
      /* noop */
    }
    setDeferredPrompt(null);
  };

  // Once installed (or running standalone), don't render anything.
  if (installState === "installed") return null;

  // Android-style native prompt is available — render the gold CTA.
  if (deferredPrompt) {
    return (
      <button
        type="button"
        onClick={triggerInstall}
        className={
          "inline-flex items-center gap-2 font-cinzel text-sm sm:text-base tracking-[0.2em] uppercase text-[#03060e] bg-[#FFE7A0] border border-[#FFE7A0] px-6 py-3 rounded-full hover:bg-[#FFE7A0] transition-colors shadow-[0_0_24px_rgba(255, 231, 160,0.45)] " +
          className
        }
        data-testid="install-pwa-button"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Install on this phone
      </button>
    );
  }

  // iOS Safari — programmatic install isn't available, but we can
  // give the user the two-tap instruction in a polished sheet.
  if (isIOSSafari) {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowIOSSheet(true)}
          className={
            "inline-flex items-center gap-2 font-cinzel text-sm sm:text-base tracking-[0.2em] uppercase text-[#FFE7A0] border border-[#FFE7A0]/60 px-6 py-3 rounded-full hover:bg-[#FFE7A0]/10 transition-colors " +
            className
          }
          data-testid="install-pwa-button-ios"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
          Add to home screen
        </button>
        {showIOSSheet && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#03060e]/85 backdrop-blur-sm px-4"
            onClick={() => setShowIOSSheet(false)}
            data-testid="install-pwa-ios-sheet"
          >
            <div
              className="w-full max-w-md bg-[#0a0f1f] border border-[#FFE7A0]/30 rounded-2xl p-6 sm:p-8 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="font-cinzel text-xs uppercase tracking-[0.3em] text-[#FFE7A0] mb-3">
                Install on iPhone
              </div>
              <p className="text-[#F0E6D2] font-body text-base leading-relaxed mb-5">
                Tap the <strong>Share</strong> button below
                <span className="inline-block align-middle mx-1">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </span>
                {" "}then choose <strong>"Add to Home Screen."</strong>
              </p>
              <p className="text-[#F0E6D2]/70 font-body text-sm mb-6">
                The Restoration Matrix will open like a real app — no browser bar, just the seal.
              </p>
              <button
                type="button"
                onClick={() => setShowIOSSheet(false)}
                className="font-cinzel text-sm tracking-[0.2em] uppercase text-[#FFE7A0] border border-[#FFE7A0]/40 px-6 py-2 rounded-full hover:bg-[#FFE7A0]/10 transition-colors"
                data-testid="install-pwa-ios-close"
              >
                Got it
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop / non-eligible browser — render nothing rather than a
  // dead button. The CTA is intentionally rare; users on a phone
  // who can install will see it, everyone else won't.
  return null;
}
